import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {t} from 'i18next';
import {Box, Typography} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import './CodingFormEditor.scss';
import {
  ICodingFormDetailsForEditResponse,
  ICodingFormControls,
} from '../../../../api/types/document.type';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import ArrCodingFormEditor from '../../../common/arr-codingform-editor/ArrCodingFormEditor';
import {CodingFormItem, ISelectItem} from '../../../../shared/types';
import {JSX} from 'react/jsx-runtime';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {useSaveCodingForm} from '../../../../hooks/document/useDocument';
import {ToastType} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import PreviewCodingForms from '../preview-coding-forms/PreviewCodingForms';

export function SortableItem(
  props: Readonly<{
    id: number;
    formData: CodingFormItem;
    allFormData: CodingFormItem[];
    handleRemoveClick: (id: number) => void;
    updateCodingFormObjects: (object: CodingFormItem) => void;
    updatedItems: CodingFormItem[];
  }>
) {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
    useSortable({
      id: props.formData.id,
      data: props.formData,
    });

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        position: 'relative',
        zIndex: isDragging ? 999 : 'auto',
        touchAction: 'none',
      }
    : undefined;

  const handleProps = {...listeners, ...attributes};

  return (
    <div
      ref={setNodeRef}
      className={`sortable-item ${isDragging ? 'is-dragging' : ''}`}
      data-testid={`coding-form-editor-div`}
    >
      <ArrCodingFormEditor
        codingFormData={props.formData}
        codingFormObjects={props.allFormData}
        handleRemoveClick={() => props.handleRemoveClick(props.id)}
        style={style}
        dragHandleProps={handleProps}
        updatedItems={props.updatedItems}
        getUpdatedData={(e) => {
          props.updateCodingFormObjects(e);
        }}
      />
    </div>
  );
}

type ActionType = 'preview' | 'validate' | 'save';

interface CodingFormEditorProps {
  codingForm?: ICodingFormDetailsForEditResponse['data'];
  selectedObjects: CodingFormItem[];
  removeCodingForm: (item: CodingFormItem) => void;
  onChange?: () => void;
  onSave?: () => void;
}

const CodingFormEditor = forwardRef(
  (
    {
      codingForm,
      selectedObjects = [],
      removeCodingForm,
      onChange = () => {},
      onSave = () => {},
    }: CodingFormEditorProps,
    ref
  ): JSX.Element => {
    const translate = (key: string) => t(`codingForms.codingFormEditor.${key}`);

    const [formName, setFormName] = useState(codingForm?.codingFormLabel ?? '');
    const [hasError, setHasError] = useState(false);
    const [supportText, setSupportText] = useState('');
    const [items, setItems] = useState<CodingFormItem[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [activeId, setActiveId] = useState<number | null>(null);
    const [errorArray, setErrorArray] = useState<string[]>([]);
    const [showValidateModal, setShowValidateModal] = useState<boolean>(false);
    const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastStatus, setToastStatus] = useState<ToastType[]>([]);

    const [updatedItems, setUpdatedItems] = useState<CodingFormItem[]>([]);
    const updatedOuputItems = useRef<ICodingFormControls[]>([]);
    const {mutate: saveCodingForm, reset: resetSaveCodingForm} =
      useSaveCodingForm();
    const [openPreviewForm, setOpenPreviewForm] = useState<boolean>(false);
    const [initialCodeId, setInitialCodeId] = useState(
      codingForm?.codingFormID ?? 0
    );
    const [isEditExecuted, setIsEditExecuted] = useState<boolean>(false);

    const nextIdRef = useRef<number>(0);

    const toGroupAssociation = (
      groupControlName?: string,
      controls?: any[]
    ): ISelectItem | null => {
      if (!groupControlName) return null;

      const groupControl = controls?.find(
        (c) =>
          c.systemControlName === groupControlName &&
          ['RADIOGROUP', 'CHECKGROUP'].includes(
            (c.contextType || '').toUpperCase()
          )
      );

      if (groupControl) {
        return {
          value: groupControl.systemControlName,
          label: `${groupControl.customControlLabel || ''} (${groupControl.systemControlName})`,
        };
      }

      // fallback if group object not found
      return {value: groupControlName, label: groupControlName};
    };

    useEffect(() => {
      if (!codingForm?.codingFormControls) {
        return;
      }
      nextIdRef.current = 0;
      if (codingForm?.codingFormControls) {
        const newItems = codingForm.codingFormControls.map((control) => {
          let itemId = Number(control.contextID);
          let value = '';
          if (control.systemControlName) {
            const parts = control.systemControlName.split('_');
            if (parts.length > 1) {
              const parsedId = Number(parts[1]);
              if (!isNaN(parsedId)) {
                itemId = parsedId;
                value = parts[1];
              }
            }
          }

          const assignedId = nextIdRef.current++;

          return {
            id: assignedId,
            cfItemID: itemId,
            sequence: control.sequence,
            type: control.contextType || '',
            specialFlag: control.isSpecial || false,
            value: value || String(itemId),
            systemLabel: control.systemControlName || '',
            friendlyLabel: control.customControlLabel || '',
            logicRuleCount: String(control.logicRuleCount || '0'),
            tagType: control.codingFormControlTypeCode || '',
            errorMessage: control.errorMessage || '',
            defaultAction: control.defaultAction || '',
            helpText: control.helpText || '',
            fieldLogic: JSON.parse(control.controlLogicJSon || '[]'),
            fieldLogicJson: control.controlLogicJSon || '',
            fieldLogicAction: control.fieldLogicAction || '',
            fieldLogicObjectLabel: `${control.customControlLabel} (${control.systemControlName})`,
            groupAssociation: toGroupAssociation(
              control.groupControlName,
              codingForm.codingFormControls
            ), //control.groupControlName || '',
            metaDataType: control.metaDataType || '',
            maxLength: control.maxLength
              ? Number(control.maxLength)
              : undefined,
            minValue: control.minValue || '',
            maxValue: control.maxValue || '',
            splitLength: control.splitLength
              ? Number(control.splitLength)
              : undefined,
            dataTypeLengthCode: control.dataTypeLengthCode || '',
            isCollapsed: true,
          };
        });
        setItems(newItems as unknown as CodingFormItem[]);
        setFormName(codingForm.codingFormLabel || '');
        setHasError(false);
        setSupportText('');
      }
    }, [codingForm]);

    useEffect(() => {
      if (!selectedObjects || selectedObjects.length === 0) {
        return;
      }
      const existingCfIds = new Set<string>();
      if (
        codingForm?.codingFormControls &&
        codingForm.codingFormControls.length > 0 &&
        items.length == 0 &&
        !isEditExecuted
      ) {
        setIsEditExecuted(true);
        codingForm.codingFormControls.forEach((control) => {
          existingCfIds.add(control.systemControlName);
        });

        items.forEach((i) => existingCfIds.add(i.systemControlName ?? ''));
      } else {
        items.forEach((i) => existingCfIds.add(i.systemLabel));
      }
      const toAppend = selectedObjects.filter(
        (so) => !existingCfIds.has(so.systemLabel)
      );
      if (toAppend.length === 0) {
        return;
      }
      setItems((prev) => {
        const startIndex = prev.length;
        const appended = toAppend.map((item, idx) => ({
          ...item,
          id: nextIdRef.current++,
          sequence: startIndex + idx + 1,
          isCollapsed: false,
        }));
        return [...prev, ...appended];
      });

      onChange?.();
    }, [selectedObjects]);

    const handleFormChange = useCallback(() => {
      onChange?.();
    }, [onChange]);

    const actionHandlers: Record<string, () => void> = {
      preview: () => setOpenPreviewForm(true),
      validate: () => validateCodingForm(),
      save: () => validateCodingForm(true),
    };

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 1,
          delay: 0,
          tolerance: 1,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    function handleDragStart(event: DragStartEvent) {
      const {active} = event;
      setActiveId(Number(active.id));
    }

    function handleDragEnd(event: DragEndEvent) {
      try {
        const {active, over} = event;

        if (!over) {
          setActiveId(null);
          return;
        }

        if (active.id !== over.id) {
          const activeId = Number(active.id);
          const overId = Number(over.id);

          const oldIndex = items.findIndex((item) => item.id === activeId);
          const newIndex = items.findIndex((item) => item.id === overId);

          if (oldIndex !== -1 && newIndex !== -1) {
            const newItems = arrayMove([...items], oldIndex, newIndex);
            const updatedItemsArr = newItems.map((item, index) => ({
              ...item,
              sequence: index + 1,
            }));

            setItems(updatedItemsArr);
            handleFormChange();
          }
        }
      } catch (error) {
        console.error('Error in drag end:', error);
      } finally {
        setActiveId(null);
      }
    }

    const handleValidationAndSubmit = (action: ActionType) => {
      const trimmedName = formName.trim();
      const specialCharRegex = /^[a-zA-Z0-9 _]*$/;
      if (!trimmedName) {
        setHasError(true);
        setSupportText(translate('errorEmptyName'));
        return;
      }

      if (!specialCharRegex.test(trimmedName)) {
        setHasError(true);
        setSupportText(translate('specialCharacterError'));
        return;
      }

      setHasError(false);
      setSupportText('');
      actionHandlers[action]?.();
    };

    const onChangeTextField = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormName(value);
      if (hasError && value.trim() !== '') {
        setHasError(false);
        setSupportText('');
      }
      onChange?.();
    };

    const updateCodingFormOutput = (object: CodingFormItem) => {
      setUpdatedItems((prevUpdatedItems) => {
        return items.map((item) => {
          const existing = prevUpdatedItems.find(
            (u) => u.systemLabel === item.systemLabel
          );

          if (item.systemLabel === object.systemLabel) {
            return {
              ...item,
              defaultAction: object.defaultAction,
              displayType: object.displayType,
              errorMessage: object.errorMessage,
              fieldLogic: object.fieldLogic,
              fieldLogicAction: object.fieldLogicAction,
              fieldLogicJson: JSON.stringify(object.fieldLogic),
              fieldLogicObjectLabel: object.fieldLogicObjectLabel,
              friendlyLabel: object.friendlyLabel,
              groupAssociation:
                object.groupAssociation ??
                existing?.groupAssociation ??
                item.groupAssociation,
              helpText: object.helpText,
              logicRuleCount: (object.fieldLogic?.length || 0).toString(),
              tagType: object.tagType,
            };
          }

          return existing ?? item;
        });
      });
    };

    const getValidationErrors = useCallback((): string[] => {
      const errorMsg: string[] = [];
      const grpAssArr: any[] = [];
      let count = 0;

      updatedOuputItems.current = updatedItems.map((object) => {
        count++;
        return {
          projectFieldName: object.projectFieldName ?? '',
          projectFieldDatatype: object.projectFieldDatatype ?? '',
          sequence: count - 1,
          contextType: object.type,
          isSpecial: object.type.toUpperCase() == 'TAG',
          contextID: object.value,
          systemControlName: object.systemLabel,
          customControlLabel: object.friendlyLabel ?? '',
          logicRuleCount: object.logicRuleCount,
          codingFormControlTypeCode: object.tagType,
          errorMessage: object.errorMessage,
          defaultAction: object.defaultAction,
          helpText: object.helpText,
          controlLogicJSon: JSON.stringify(object.fieldLogic),
          groupControlName: object.groupAssociation?.value ?? '',
          fieldLogicAction: object.fieldLogicAction,
          controlValue: '',
          controlDataType: object.tagType,
          metaDataType: object.metaDataType ?? '',
          maxLength: object.maxLength ?? 0,
          minValue: object.minValue ?? '',
          maxValue: object.maxValue ?? '',
          splitLength: object.splitLength,
          dataTypeLengthCode: object.dataTypeLengthCode ?? '',
          aiPredictionClassification: object.aiPredictionClassification ?? '',
          aiPredictionScore: object.aiPredictionScore ?? 0,
          displayType: object.displayType,
        } as ICodingFormControls;
      });

      if (updatedOuputItems.current.length === 0) {
        errorMsg.push(t('codingForms.editor.noObjectMsg'));
      }

      updatedOuputItems.current.forEach((formObject) => {
        if (
          formObject.contextType.toLowerCase() === 'radiogroup' ||
          formObject.contextType.toLowerCase() === 'checkgroup'
        ) {
          const itm: any = {};
          itm.GrpName = formObject.systemControlName;
          itm.GrpCnt = 0;
          grpAssArr.push(itm);
        }
      });

      updatedOuputItems.current.forEach((formObject) => {
        if (
          formObject.customControlLabel == undefined ||
          formObject.customControlLabel == ''
        ) {
          errorMsg.push(
            t('codingForms.editor.frndlLabelMsg') +
              ' ' +
              formObject.contextType +
              ':' +
              formObject.systemControlName
          );
        }

        if (
          Number(formObject.logicRuleCount) > 0 &&
          (formObject.fieldLogicAction == undefined ||
            formObject.fieldLogicAction == null ||
            formObject.fieldLogicAction == '')
        ) {
          errorMsg.push(
            t('codingForms.editor.fLogicActionMsg') +
              ' ' +
              formObject.contextType +
              ':' +
              formObject.customControlLabel +
              '(' +
              formObject.systemControlName +
              ')'
          );
        }

        if (
          formObject.groupControlName != undefined &&
          formObject.groupControlName !== ''
        ) {
          grpAssArr.forEach((grpAss) => {
            if (grpAss.GrpName == formObject.groupControlName) {
              grpAss.GrpCnt = grpAss.GrpCnt + 1;
            }
          });
        }

        const objArr: string[] = [];
        const fieldLogicArray: {
          objectname: string;
          condition: string;
          operator: string;
        }[] = JSON.parse(formObject.controlLogicJSon || '[]');
        fieldLogicArray.forEach((fieldLogic, index) => {
          if (
            fieldLogic.objectname == undefined ||
            fieldLogic.objectname == ''
          ) {
            errorMsg.push(
              t('codingForms.editor.fLogicObjectMsg') +
                ' ' +
                t('codingForms.editor.objectLabel') +
                (index + 1) +
                ' in ' +
                formObject.contextType +
                ':' +
                formObject.customControlLabel +
                '(' +
                formObject.systemControlName +
                ')'
            );
          } else {
            if (objArr.includes(fieldLogic.objectname)) {
              errorMsg.push(
                t('codingForms.editor.fLogicDuplicateMsg') +
                  ' ' +
                  formObject.contextType +
                  ':' +
                  formObject.customControlLabel +
                  '(' +
                  formObject.systemControlName +
                  ')'
              );
            } else {
              objArr.push(fieldLogic.objectname);
            }
          }

          if (fieldLogic.condition == undefined || fieldLogic.condition == '') {
            errorMsg.push(
              t('codingForms.editor.fLogicCondotionMsg') +
                ' ' +
                t('codingForms.editor.objectLabel') +
                (index + 1) +
                ' in ' +
                formObject.contextType +
                ':' +
                formObject.customControlLabel +
                '(' +
                formObject.systemControlName +
                ')'
            );
          }
        });
      });

      grpAssArr.forEach((grpAss) => {
        if (grpAss.GrpCnt < 1) {
          errorMsg.push(
            t('codingForms.editor.addObjMsg') +
              ' ' +
              grpAss.GrpName +
              ' ' +
              t('codingForms.editor.group')
          );
        }
      });

      if (errorMsg.length < 1) {
        errorMsg.push(t('codingForms.editor.validationSuccessMsg'));
      }

      return errorMsg;
    }, [updatedItems, t]);

    const buildSavePayload = useCallback(() => {
      const payload = {
        codingFormID: initialCodeId,
        codingFormName: formName.trim(),
        relationJSon: JSON.stringify(items.map((_, index) => ({id: index}))),
        codingFormControls: updatedOuputItems.current,
      };
      return payload;
    }, [codingForm, formName, items, initialCodeId]);

    const triggerToast = (message: string, status: ToastType[]) => {
      setToastMessage(message);
      setToastStatus(status);
      setShowToast(true);
    };

    const saveProgrammatic = useCallback((): Promise<void> => {
      return new Promise((resolve, reject) => {
        try {
          const trimmedName = formName.trim();
          const specialCharRegex = /^[a-zA-Z0-9 _]*$/;
          if (!trimmedName) {
            setHasError(true);
            setSupportText(translate('errorEmptyName'));
            reject(new Error('Validation failed'));
            return;
          }

          if (!specialCharRegex.test(trimmedName)) {
            setHasError(true);
            setSupportText(translate('specialCharacterError'));
            reject(new Error('Validation failed'));
            return;
          }

          const payload = buildSavePayload();
          resetSaveCodingForm();
          saveCodingForm(payload, {
            onSuccess: () => {
              triggerToast(
                payload.codingFormID > 0
                  ? t('codingForms.editor.updateSuccess')
                  : t('codingForms.editor.saveSuccess'),
                [ToastType.Success]
              );
              setShowSaveModal(false);
              onSave?.();
              resolve();
            },
            onError: (error) => {
              triggerToast(
                error?.message ?? t('codingForms.editor.errorSaveForm'),
                [ToastType.Error]
              );
              setShowSaveModal(false);
              reject(error);
            },
          });
        } catch (err) {
          reject(err);
        }
      });
    }, [
      formName,
      buildSavePayload,
      resetSaveCodingForm,
      saveCodingForm,
      onSave,
      t,
    ]);

    const validateCodingForm = (fromSave: boolean = false) => {
      const errors = getValidationErrors();
      setErrorArray(errors);
      if (fromSave) {
        setShowSaveModal(true);
      } else {
        setShowValidateModal(true);
      }
    };

    const handleSaveCodingForm = () => {
      resetSaveCodingForm();
      const payload = buildSavePayload();
      saveCodingForm(payload, {
        onSuccess: async (response) => {
          setInitialCodeId(response.data);
          triggerToast(
            payload.codingFormID > 0
              ? t('codingForms.editor.updateSuccess')
              : t('codingForms.editor.saveSuccess'),
            [ToastType.Success]
          );
          setShowSaveModal(false);
          onSave?.();
        },
        onError: (error) => {
          triggerToast(error.message ?? t('codingForms.editor.errorSaveForm'), [
            ToastType.Error,
          ]);
          setShowSaveModal(false);
        },
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        save: async () => {
          getValidationErrors();
          await saveProgrammatic();
        },
        resetDirty: () => {
          setErrorArray([]);
          setShowSaveModal(false);
          setShowValidateModal(false);
          setHasError(false);
        },
        forceReset: () => {
          setItems([]);
          setFormName('');
          setErrorArray([]);
          setShowSaveModal(false);
          setShowValidateModal(false);
          setHasError(false);
        },
      }),
      [saveProgrammatic]
    );

    const returnValue = (
      <Box className="coding-form-editor-container">
        <Box className="text-area">
          <Typography
            className="typo-text subhead2_size"
            data-testid={`coding-form-editor-title`}
          >
            {translate('title')}
          </Typography>
          <ArrTooltipWrapper
            title={translate('toolTipTitle')}
            trigger={<InfoOutlined fontSize="medium" className="alert-icon" />}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: translate('description'),
              }}
              data-testid={`coding-form-editor-div-btn`}
            />
          </ArrTooltipWrapper>
        </Box>

        <Box className="coding-form-editor-border" />
        <Box className="create-form">
          <ArrTextField
            fullWidth
            sizeVariant="medium"
            className="text-box"
            aria-invalid={hasError}
            aria-describedby={hasError ? 'form-name-error' : undefined}
            value={formName}
            placeholder={translate('formName')}
            onChange={onChangeTextField}
            state={hasError ? 'error' : 'enabled'}
            supportText={hasError ? supportText : ''}
            showErrorIcon={true}
            dataTestId="coding-form-name"
          />
          <Box className="create-form-buttons">
            <ArrButton
              aria-label={translate('preview')}
              variant="secondary"
              size="small"
              onClick={() => handleValidationAndSubmit('preview')}
              dataTestId="coding-form-editor-preview-button"
            >
              {translate('preview')}
            </ArrButton>
            <ArrButton
              aria-label={translate('validate')}
              variant="secondary"
              size="small"
              onClick={() => handleValidationAndSubmit('validate')}
              dataTestId="coding-form-editor-validate-button"
            >
              {translate('validate')}
            </ArrButton>
            <ArrButton
              aria-label={translate('save')}
              variant="primary"
              size="small"
              onClick={() => handleValidationAndSubmit('save')}
              dataTestId="coding-form-editor-save-button"
            >
              {translate('save')}
            </ArrButton>
          </Box>
        </Box>

        <Box className="coding-form-editor-canvas">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <SortableContext
              items={items.map((item) => item.id)}
              strategy={rectSortingStrategy}
              id="coding-form-items"
            >
              {items.map((item) => (
                <SortableItem
                  key={item.id}
                  id={item.id}
                  formData={item}
                  allFormData={items}
                  updatedItems={updatedItems}
                  handleRemoveClick={(id) => {
                    const itemToRemove = items.find(
                      (item) => item.id === Number(id)
                    );
                    if (itemToRemove) {
                      setItems((prevItems) => {
                        const filteredItems = prevItems.filter(
                          (i) => i.id !== Number(id)
                        );
                        return filteredItems.map((item, index) => ({
                          ...item,
                          sequence: index + 1,
                        }));
                      });
                      setUpdatedItems((prev) =>
                        prev.filter(
                          (u) => u.systemLabel !== itemToRemove.systemLabel
                        )
                      );
                      removeCodingForm(itemToRemove);
                      handleFormChange();
                    }
                  }}
                  updateCodingFormObjects={updateCodingFormOutput}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>

        <ArrModal
          showModal={showValidateModal}
          onClose={() => setShowValidateModal(false)}
          headerTitle={t('codingForms.editor.validationModalTitle')}
          className="validation-modal"
          dataTestId="cfe-validation-modal"
          buttons={[
            {
              id: '1',
              label: t('common.ok').toUpperCase(),
              onClick: () => setShowValidateModal(false),
              variant: 'primary',
              dataTestId: 'copy-modal-confirm-btn',
            },
          ]}
        >
          <Box className="modal-container">
            <ul data-testid={`coding-form-editor-ul`}>
              {errorArray.map((errorMsg, index) => (
                <li
                  className="typo-text body2_size text-body-strong"
                  key={index + 1}
                  data-testid={`coding-form-editor-li-${index}`}
                >
                  {errorMsg}
                </li>
              ))}
            </ul>
          </Box>
        </ArrModal>

        <ArrModal
          showModal={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          headerTitle={t('codingForms.editor.saveModalTitle')}
          className="save-modal"
          dataTestId="cfe-save-modal"
          buttons={[
            {
              id: '1',
              label: t('common.no'),
              onClick: () => setShowSaveModal(false),
              variant: 'secondary',
              dataTestId: 'copy-modal-no-btn',
            },
            {
              id: '2',
              label: t('common.yes'),
              onClick: () => {
                handleSaveCodingForm();
              },
              variant: 'primary',
              dataTestId: 'copy-modal-yes-btn',
            },
          ]}
        >
          <Box className="modal-container">
            <Box className="modal-body">
              <Typography
                className="typo-text subhead4_size validate-head text-body-strong"
                data-testid={`coding-form-editor-validation-status`}
              >
                {t('codingForms.editor.validationStatus')}
              </Typography>

              <ul
                data-testid={`coding-form-editor-ul-btn`}
                className={'modalList'}
              >
                {errorArray.map((errorMsg, index) => (
                  <li
                    className="typo-text body2_size error-text text-body-strong"
                    key={index + 1}
                    data-testid={`coding-form-editor-li-error-${index}`}
                  >
                    {errorMsg}
                  </li>
                ))}
              </ul>
            </Box>
            <Typography
              className="typo-text body2_size error-text text-body-strong"
              data-testid={`coding-form-editor-continue-msg`}
            >
              {t('codingForms.editor.continueMsg')}
            </Typography>
          </Box>
        </ArrModal>

        {showToast && (
          <ArrToastNotification
            message={toastMessage}
            status={toastStatus}
            onClose={() => setShowToast(false)}
          />
        )}
        {openPreviewForm && (
          <PreviewCodingForms
            open={openPreviewForm}
            handleDrawer={(value) => setOpenPreviewForm(value)}
            items={updatedItems}
            formName={formName}
          />
        )}
      </Box>
    );

    return returnValue;
  }
);

export default CodingFormEditor;
